import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GameScene from '../game/GameScene';
import {
  CompassStrip,
  VitalsStrip,
  AtmospherePanel,
  MiniRadar,
  InventoryMiniPanel,
  DamageVignette,
  DeathOverlay,
  MiningPrompt,
  MiningProgress,
  ShipDockPrompt,
  CityEnterPrompt,
  LightningFlash,
} from '../components/hud/HudWidgets';
import { usePlayerState } from '../hooks/usePlayerState';
import InventoryOverlay from '../components/InventoryOverlay';
import CityOverlay from '../components/CityOverlay';
import StationOverlay from '../components/StationOverlay';
import SettingsModal from '../components/SettingsModal';
import BuildMenu from '../components/BuildMenu';
import StructureInteractOverlay from '../components/StructureInteractOverlay';
import HelpOverlay from '../components/HelpOverlay';
import HangarOverlay from '../components/HangarOverlay';
import SkillTreeOverlay from '../components/SkillTreeOverlay';
import ModulesOverlay from '../components/ModulesOverlay';
import CraftingOverlay from '../components/CraftingOverlay';
import DerelictOverlay from '../components/DerelictOverlay';
import { getAudioSystem } from '../game/AudioSystem';
import { useUserSettings } from '../components/SettingsModal';
import { toast } from 'sonner';

/**
 * Explore3D
 *
 * Phase 1 MVP gameplay page. Loads the persisted system, locates the target
 * planet (and optional moon), spins up a Babylon.js GameScene on a canvas,
 * and renders a sci-fi HUD overlay with controls + state.
 *
 * Routes:
 *   /play/:coords/planet/:planetIdx
 *   /play/:coords/planet/:planetIdx/moon/:moonIdx
 */
function Explore3D() {
  const { coords, planetIdx, moonIdx } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user, updateCredits } = useAuth();
  const player = usePlayerState(api);

  const canvasRef = useRef(null);
  const sceneRef = useRef(null);

  // Stable refs to avoid re-creating the GameScene on every render of `player`,
  // `api`, or `updateCredits`. These keep the scene mount effect's dep list small
  // (only loading/error/body/route params) and prevent an infinite render loop.
  const playerRef = useRef(player);
  const apiRef = useRef(api);
  const updateCreditsRef = useRef(updateCredits);
  const routeRef = useRef({ coords, planetIdx, moonIdx });
  const derelictBoardedIdRef = useRef(null);
  const graphicsQualityRef = useRef('cinematic');
  const claimedDiscoveriesRef = useRef([]);
  useEffect(() => { playerRef.current = player; }, [player]);
  useEffect(() => { apiRef.current = api; }, [api]);
  useEffect(() => { updateCreditsRef.current = updateCredits; }, [updateCredits]);
  useEffect(() => { routeRef.current = { coords, planetIdx, moonIdx }; }, [coords, planetIdx, moonIdx]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [body, setBody] = useState(null);
  const [deathSummary, setDeathSummary] = useState(null);
  const [respawning, setRespawning] = useState(false);
  // Bumping this forces the GameScene mount effect to dispose and re-create
  // the Babylon scene, picking up any hot-reloaded engine code without a full
  // browser refresh.
  const [sceneBuildKey, setSceneBuildKey] = useState(0);
  // Orbital station dock overlay state
  const [stationOpen, setStationOpen] = useState(false);
  const [dockedStation, setDockedStation] = useState(null);
  const [hudState, setHudState] = useState({
    cameraMode: 'fps',
    shipCameraMode: 'tps',
    nearShip: false,
    inShip: false,
    pointerLocked: false,
    pointerLockUnavailable: false,
    shipSpeed: 0,
    shipAltitude: 0,
    shipThrottle: 0,
    shipBoost: false,
    canExit: true,
    exitDenied: false,
    gravity: 9.8,
    atmosphere: 'none',
    isGrounded: true,
    mode: 'surface',
    canLaunch: false,
    approachTarget: null,
    nearestBodyName: null,
    nearestBodyDistance: 0,
    bodyList: [],
    // Phase 6 HUD additions
    heading: 0,
    coords: { x: 0, y: 0, z: 0 },
    biomeLabel: '',
    oxygen: 100,
    maxOxygen: 100,
    oxygenDepleting: false,
    breathable: true,
    shipBearing: 0,
    shipDistance: 0,
    pois: [],
  });
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Phase 11 — nanites + structures
  const [naniteCatalog, setNaniteCatalog] = useState([]);
  const [naniteBalance, setNaniteBalance] = useState(0);
  const [buildMenuOpen, setBuildMenuOpen] = useState(false);
  const [buildState, setBuildState] = useState({ active: false, typeId: null, valid: false });
  const [structures, setStructures] = useState([]);
  const [structureOverlayDoc, setStructureOverlayDoc] = useState(null);
  const [discoveryLog, setDiscoveryLog] = useState(null);
  // Phase 13 — F1 help overlay
  const [helpOpen, setHelpOpen] = useState(false);
  // June 2026 — Hangar / Shipyard
  const [hangarOpen, setHangarOpen] = useState(false);
  const [modulesOpen, setModulesOpen] = useState(false);
  const [craftingOpen, setCraftingOpen] = useState(false);
  const [craftingSource, setCraftingSource] = useState('ship');
  const [activeShip, setActiveShip] = useState(null);   // null until garage loads
  const [shipDef, setShipDef] = useState(null);
  const shipCatalogRef = useRef([]);
  // June 2026 — Skill Tree
  const [skillTreeOpen, setSkillTreeOpen] = useState(false);
  // Phase 3 — Derelict ships & shipwrecks
  const [derelictOpen, setDerelictOpen] = useState(false);
  const [derelictBoardedId, setDerelictBoardedId] = useState(null);
  const [surfaceDerelicts, setSurfaceDerelicts] = useState([]);
  const [spaceDerelicts, setSpaceDerelicts] = useState([]);
  useEffect(() => { derelictBoardedIdRef.current = derelictBoardedId; }, [derelictBoardedId]);  const [progression, setProgression] = useState({ walkMult: 1, shipSpeedMult: 1, jumpMult: 1 });
  const progressionRef = useRef(progression);
  useEffect(() => { progressionRef.current = progression; }, [progression]);
  // Phase 12c — initial tab for the City overlay when opened from a kiosk
  const [cityInitialTab, setCityInitialTab] = useState(null);
  const userSettings = useUserSettings();

  // Apply user settings to scene + HUD when they change
  useEffect(() => {
    graphicsQualityRef.current = userSettings.graphics_quality || 'cinematic';
    if (sceneRef.current) {
      sceneRef.current.setMouseConfig?.({
        sensitivity: userSettings.mouse_sensitivity,
        invertY: userSettings.invert_y,
      });
      sceneRef.current.setGraphicsQuality?.(userSettings.graphics_quality);
    }
    try {
      document.documentElement.style.setProperty('--hud-opacity', String(userSettings.hud_opacity || 1));
    } catch (_e) { /* noop */ }
    try {
      getAudioSystem().setVolumes({
        master: userSettings.master_volume,
        sfx: userSettings.sfx_volume,
        ambient: userSettings.ambient_volume,
      });
    } catch (_e) { /* noop */ }
  }, [userSettings]);

  // Phase 10 — register player visit (powers explore_bodies contracts) +
  // start procedural ambient audio while on a planet/moon scene.
  useEffect(() => {
    if (!body || !coords) return;
    let cancelled = false;
    (async () => {
      try {
        await api.post('/api/player/visit', {
          coords_key: coords,
          planet_idx: Number(planetIdx),
          moon_idx: moonIdx != null ? Number(moonIdx) : null,
        });
      } catch (_e) { /* noop */ }
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [api, body, coords, planetIdx, moonIdx]);

  // Phase 10 — Audio: load user volume settings + start ambient drone.
  useEffect(() => {
    const audio = getAudioSystem();
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/settings/me');
        if (cancelled || !data) return;
        audio.setVolumes({
          master: data.master_volume,
          sfx: data.sfx_volume,
          ambient: data.ambient_volume,
        });
      } catch (_e) { /* noop */ }
    })();
    // Start the ambient bed lazily — the first user gesture unlocks WebAudio.
    const onFirstGesture = () => {
      try { audio.startAmbient(); } catch (_e) { /* noop */ }
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
    window.addEventListener('pointerdown', onFirstGesture);
    window.addEventListener('keydown', onFirstGesture);
    return () => {
      cancelled = true;
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
      try { audio.stopAmbient(); } catch (_e) { /* noop */ }
    };
  }, [api]);

  // June 2026 — swap to cave ambience when underground, surface bed otherwise.
  const caveAudioRef = useRef(false);
  useEffect(() => {
    const audio = getAudioSystem();
    const was = caveAudioRef.current;
    if (hudState.inCave && !was) {
      caveAudioRef.current = true;
      try { audio.stopAmbient(); audio.startCaveAmbient(); } catch (_e) { /* noop */ }
    } else if (!hudState.inCave && was) {
      caveAudioRef.current = false;
      try { audio.stopCaveAmbient(); audio.startAmbient(); } catch (_e) { /* noop */ }
    }
  }, [hudState.inCave]);


  // Toggle inventory overlay with `I` (and close with Escape). Also pauses
  // GameScene input so WASD doesn't drift while the modal is up.
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing into an input/textarea/contentEditable (future-proof)
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'i') {
        e.preventDefault?.();
        setInventoryOpen((prev) => !prev);
      } else if (k === 'escape' && inventoryOpen) {
        e.preventDefault?.();
        setInventoryOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inventoryOpen]);

  // Pause/resume GameScene input now lives in the combined effect further
  // down (it watches cityOpen + inventoryOpen together to avoid races).

  // Load system + body
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.post('/api/systems/persist', { coords });
        if (cancelled) return;
        const system = response.data;
        const pIdx = parseInt(planetIdx, 10);
        const planet = system?.planets?.[pIdx];
        if (!planet) {
          setError('Planet not found in this system.');
          setLoading(false);
          return;
        }
        // System-level celestial context
        const starInfo = {
          color: system.star_color || '#FFE4A0',
          class: system.star_class || 'G',
          name: system.coords_key,
        };
        // Full system layout for Phase 5 (system travel). Map each planet +
        // its moons into a flat list of bodies with orbital info, so the
        // gameplay scene can render them in space.
        const systemLayout = {
          star: starInfo,
          bodies: Array.isArray(system.planets) ? system.planets.flatMap((pl, idx) => {
            const base = {
              kind: 'planet',
              planetIdx: idx,
              name: pl.custom_name || `Planet ${idx + 1}`,
              type: pl.type,
              color: pl.color || '#7a8aa0',
              radius_km: pl.radius_km,
              orbit_au: pl.orbit_au,
              initial_angle: pl.initial_angle ?? (idx * 0.7),
              atmosphere: pl.atmosphere,
              habitability: pl.habitability,
              isCurrent: idx === pIdx && moonIdx === undefined,
            };
            const moonBodies = Array.isArray(pl.moons) ? pl.moons.map((mn, mi) => ({
              kind: 'moon',
              planetIdx: idx,
              moonIdx: mi,
              name: mn.custom_name || mn.name || `Moon ${mi + 1}`,
              type: 'Moon',
              color: '#a8a39d',
              radius_km: mn.radius_km,
              parentOrbit_au: pl.orbit_au,
              parentInitialAngle: pl.initial_angle ?? (idx * 0.7),
              parentName: base.name,
              moonAngle: (mi / Math.max(1, pl.moons.length)) * Math.PI * 2,
              isCurrent: idx === pIdx && moonIdx !== undefined && parseInt(moonIdx, 10) === mi,
            })) : [];
            return [base, ...moonBodies];
          }) : [],
        };
        // Collect element names found via any prior scan (any user) of this
        // body — Phase 8 mining is gated to ONLY these elements.
        const collectScannedElementNames = (scansList) => {
          const set = new Set();
          for (const s of (scansList || [])) {
            // Backend stores scan results under `scan_data.elements_found`
            // (each item is `{ name, quantity_kg, rarity }`). We also fall back
            // to `elements` for safety in case the shape ever changes.
            const els = s?.scan_data?.elements_found || s?.scan_data?.elements || [];
            for (const e of els) {
              if (typeof e === 'string') {
                set.add(e);
              } else if (e?.name) {
                set.add(e.name);
              }
            }
          }
          return Array.from(set);
        };
        let resolvedBody;
        if (moonIdx !== undefined) {
          const mIdx = parseInt(moonIdx, 10);
          const moon = planet.moons?.[mIdx];
          if (!moon) {
            setError('Moon not found on this planet.');
            setLoading(false);
            return;
          }
          resolvedBody = {
            kind: 'moon',
            type: 'Moon',
            name: moon.custom_name || moon.name || `Moon ${mIdx + 1}`,
            parentPlanet: planet.custom_name || `Planet ${pIdx + 1}`,
            parentColor: planet.color || '#7a8aa0',
            parentType: planet.type,
            parentRadiusKm: planet.radius_km,
            atmosphere: 'None',
            habitability: 0,
            radius_km: moon.radius_km,
            scans: moon.scans || [],
            scannedElements: collectScannedElementNames(moon.scans),
            star: starInfo,
            moons: [],
            systemLayout,
            currentPlanetIdx: pIdx,
            currentMoonIdx: mIdx,
            coords,
          };
        } else {
          resolvedBody = {
            kind: 'planet',
            type: planet.type,
            name: planet.custom_name || `Planet ${pIdx + 1}`,
            atmosphere: planet.atmosphere,
            habitability: planet.habitability,
            radius_km: planet.radius_km,
            color: planet.color,
            scans: planet.scans || [],
            scannedElements: collectScannedElementNames(planet.scans),
            star: starInfo,
            moons: Array.isArray(planet.moons) ? planet.moons.map((mn, i) => ({
              name: mn.custom_name || mn.name || `Moon ${i + 1}`,
              radius_km: mn.radius_km,
              orbit_km: mn.orbit_km,
              type: mn.type || 'Moon',
              color: mn.color || '#a8a39d',
            })) : [],
            systemLayout,
            currentPlanetIdx: pIdx,
            coords,
          };
        }
        // Phase 4 — attach legendary classification so the 3D engine can render
        // the wonder's unique landmark + guaranteed rich ore cluster.
        try {
          const legParams = moonIdx !== undefined
            ? { coords_key: coords, planet_idx: pIdx, moon_idx: parseInt(moonIdx, 10) }
            : { coords_key: coords, planet_idx: pIdx };
          const legRes = await api.get('/api/legendary/check', { params: legParams });
          resolvedBody.legendary = legRes?.data || { is_legendary: false };
        } catch (_e) {
          resolvedBody.legendary = { is_legendary: false };
        }
        setBody(resolvedBody);
        setLoading(false);
      } catch (e) {
        console.error('Failed to load system for gameplay', e);
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Failed to load system');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [coords, planetIdx, moonIdx, api]);

  // Mount Babylon scene — must only re-run when the actual body changes,
  // never on every render of player/api/updateCredits (those go through refs).
  useEffect(() => {
    if (loading || error || !body || !canvasRef.current || !activeShip) return;
    let disposed = false;
    const scene = new GameScene(canvasRef.current, {
      body,
      activeShip,
      shipDef,
      progression: progressionRef.current,
      onStateChange: (s) => {
        if (!disposed) setHudState(s);
      },
      onLandRequest: (targetBody) => {
        if (disposed) return;
        // Navigate React Router to the target body's surface route → fresh scene
        if (targetBody?.kind === 'moon') {
          navigate(
            `/play/${coords}/planet/${targetBody.planetIdx}/moon/${targetBody.moonIdx}`,
            { state: { from: `/system/${coords}` } },
          );
        } else if (targetBody?.kind === 'planet') {
          navigate(
            `/play/${coords}/planet/${targetBody.planetIdx}`,
            { state: { from: `/system/${coords}` } },
          );
        }
      },
      // Phase 7 — combat events bubbled up from CombatSystem (use refs so we
      // always reach the latest player/api without invalidating this effect).
      onDamageTaken: (info) => {
        try { getAudioSystem().playHurt(); } catch (_e) { /* noop */ }
        playerRef.current?.queueStatePatch?.({ hp: info.hp, shield: info.shield });
      },
      onVitalsRegen: (info) => {
        // Persist regenerating vitals to the backend so leaving + re-entering
        // a scene doesn't snap the bars back to their pre-regen values.
        playerRef.current?.queueStatePatch?.({ hp: info.hp, shield: info.shield });
      },
      onKill: async (info) => {
        try { getAudioSystem().playUi(); } catch (_e) { /* noop */ }
        try {
          const r = routeRef.current;
          const { data } = await apiRef.current.post('/api/player/loot', {
            credits: info.credits || 0,
            ore: null,
            coords_key: r.coords,
            planet_idx: Number(r.planetIdx),
            moon_idx: r.moonIdx !== undefined ? Number(r.moonIdx) : null,
            elite: !!info.elite,
            tile_x: info.tile_x ?? null,
            tile_z: info.tile_z ?? null,
            archetype: info.archetype ?? null,
          });
          if (data?.credits != null) updateCreditsRef.current?.(data.credits);
          toast.success(`${info.elite ? '★ ' : ''}Eliminated ${info.name} · +${info.credits} cr`);
          (data?.rep_changes || []).forEach((rc) => {
            const sign = rc.delta > 0 ? '+' : '';
            (rc.delta > 0 ? toast.success : toast.warning)(`${rc.short} reputation ${sign}${rc.delta} → ${rc.rep}`);
          });
        } catch (_e) {
          toast(`Eliminated ${info.name}`);
        }
      },
      onDeath: async (_info) => {
        if (disposed) return;
        const pos = sceneRef.current?.getState?.()?.coords || { x: 0, y: 0, z: 0 };
        const r = routeRef.current;
        try {
          const { data } = await apiRef.current.post('/api/player/die', {
            coords_key: r.coords,
            planet_idx: Number(r.planetIdx),
            moon_idx: r.moonIdx !== undefined ? Number(r.moonIdx) : null,
            pos,
            cause: 'combat',
          });
          if (data?.credits != null) updateCreditsRef.current?.(data.credits);
          setDeathSummary({
            credits_lost: data?.credits_lost ?? 0,
            cargo_count: data?.dropped_cache?.ores?.length ?? 0,
          });
          playerRef.current?.refreshInventory?.();
          playerRef.current?.refreshDeaths?.();
        } catch (_e) {
          setDeathSummary({ credits_lost: 0, cargo_count: 0 });
        }
      },
      // Phase 8 — mining extraction: add 1 unit of the element to cargo (capacity-respecting)
      onMiningExtract: async (info) => {
        if (disposed) return;
        const el = info?.deposit?.element;
        if (!el) return;
        try { getAudioSystem().playMineExtract(); } catch (_e) { /* noop */ }
        const cur = playerRef.current?.inventory;
        const ores = Array.isArray(cur?.ores) ? cur.ores.map((o) => ({ ...o })) : [];
        let stack = ores.find((o) => o.symbol === el.symbol);
        if (!stack) {
          stack = {
            symbol: el.symbol,
            name: el.name,
            qty: 0,
            est_value_per_unit: Number(el.value_per_kg || 0),
          };
          ores.push(stack);
        }
        stack.qty = Number(stack.qty || 0) + (info.unitsExtracted || 1);
        const newInv = await playerRef.current?.mutateInventory?.({ ores });
        if (newInv) {
          const used = (newInv.ores || []).reduce((a, o) => a + (o.qty || 0), 0);
          if (used >= (newInv.cargo_capacity || 0)) {
            toast(`Cargo full — ${el.symbol} added at capacity limit`);
          } else {
            toast.success(`Mined +${info.unitsExtracted || 1}u ${el.name}`);
          }
        }
      },
      // Phase 10 — fire SFX, bubbled up from CombatSystem
      onFire: () => {
        try { getAudioSystem().playLaser(); } catch (_e) { /* noop */ }
      },
      // Phase 3 — a guarded wreck's drones were all destroyed: persist the clear.
      onDerelictCleared: async (id) => {
        try {
          await apiRef.current.post('/api/derelicts/clear', { id });
          toast.success('Wreck secured · hostiles eliminated', { icon: '🛰️' });
        } catch (_e) { /* noop */ }
      },
    });
    sceneRef.current = scene;
    return () => {
      disposed = true;
      try { scene.dispose(); } catch (e) { /* noop */ }
      sceneRef.current = null;
    };
    // IMPORTANT: do NOT add player/api/updateCredits here — they would
    // re-create the scene on every render and trigger an infinite loop.
    // `sceneBuildKey` is included so a manual rebuild (e.g. after an engine
    // hot-reload) can re-run this effect on demand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, body, coords, navigate, sceneBuildKey, activeShip]);

  // Sync persisted vitals into the combat system whenever they change.
  // Only depend on the primitive vitals fields so we don't re-run on every
  // unrelated player object reference change.
  const persistedHp = player.state?.hp;
  const persistedShield = player.state?.shield;
  const persistedMaxHp = player.state?.max_hp;
  const persistedMaxShield = player.state?.max_shield;
  useEffect(() => {
    if (persistedHp == null) return;
    sceneRef.current?.setPlayerVitals?.({
      hp: persistedHp,
      maxHp: persistedMaxHp,
      shield: persistedShield,
      maxShield: persistedMaxShield,
    });
  }, [persistedHp, persistedShield, persistedMaxHp, persistedMaxShield]);

  // Phase 8 — fetch the element catalog once and push it into the scene so
  // MiningSystem can seed deterministic deposits with canonical element data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/mining/elements');
        if (cancelled) return;
        sceneRef.current?.setMiningElements?.(data?.elements || []);
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [api, body]);

  // Phase 9 — fetch city for this body (planets only) and push to scene.
  // Backend gates which bodies qualify; non-qualifying bodies return 4xx →
  // we silently render no city in that case.
  const [city, setCity] = useState(null);
  const [cityOpen, setCityOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setCity(null);
    sceneRef.current?.clearCity?.();
    if (!body || body.kind !== 'planet' || !coords || planetIdx == null) return;
    (async () => {
      try {
        const { data } = await api.get('/api/city', {
          params: { coords_key: coords, planet_idx: Number(planetIdx) },
        });
        if (cancelled) return;
        setCity(data);
        sceneRef.current?.setCity?.(data);
      } catch (_e) {
        // No city here — body doesn't qualify or system not colonized.
      }
    })();
    return () => { cancelled = true; };
  }, [api, body, coords, planetIdx]);

  // E key — open City overlay when standing in the plaza
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      // Phase 13 — F1 toggles help overlay
      if (e.key === 'F1') {
        e.preventDefault?.();
        setHelpOpen((v) => !v);
        return;
      }
      // June 2026 — G boards the active ship's walkable cabin (large ships).
      if (k === 'g') {
        const hud = sceneRef.current?.getState?.();
        if (hud?.canBoardInterior && !hud?.inShipInterior) {
          e.preventDefault?.();
          sceneRef.current?.enterShipInterior?.();
        }
        return;
      }
      if (k === 'e') {
        const hud = sceneRef.current?.getState?.();
        // June 2026 — Ship interior takes top priority while aboard the cabin.
        if (hud?.inShipInterior) {
          if (hud?.nearShipFabricator) {
            e.preventDefault?.();
            setCraftingSource('ship');
            setCraftingOpen(true);
            return;
          }
          if (hud?.nearShipInteriorExit) {
            e.preventDefault?.();
            sceneRef.current?.exitShipInterior?.();
          }
          return;
        }
        // June 2026 — Station interior takes top priority while walking inside.
        if (hud?.inStation) {
          if (hud?.nearStationShipyard) {
            e.preventDefault?.();
            setHangarOpen(true);
            return;
          }
          if (hud?.nearStationSkills) {
            e.preventDefault?.();
            setSkillTreeOpen(true);
            return;
          }
          if (hud?.nearStationTerminal) {
            e.preventDefault?.();
            setDockedStation(hud.stationInfo);
            setStationOpen(true);
            return;
          }
          if (hud?.nearStationExit) {
            e.preventDefault?.();
            sceneRef.current?.exitStation?.();
            return;
          }
          return;
        }
        // June 2026 — Caves take priority while on foot.
        if (hud?.inCave) {
          if (hud?.nearCaveExit) {
            e.preventDefault?.();
            sceneRef.current?.exitCave?.();
            return;
          }
        } else if (hud?.nearCaveEntrance && !hud?.inShip) {
          e.preventDefault?.();
          sceneRef.current?.enterCave?.();
          return;
        }
        // Phase 3 — Derelicts: aboard interactions, else board the nearest wreck.
        if (hud?.inDerelict) {
          if (hud?.nearDerelictContainer?.id) {
            e.preventDefault?.();
            const cid = hud.nearDerelictContainer.id;
            (async () => {
              try {
                const { data } = await apiRef.current.post('/api/derelicts/loot', {
                  id: derelictBoardedIdRef.current,
                  container_id: cid,
                });
                if (data?.credits != null) updateCreditsRef.current?.(data.credits);
                sceneRef.current?.markDerelictContainerLooted?.(cid);
                const summary = (data?.summary || []).join(' · ');
                toast.success(`Looted · ${summary || 'empty'}`);
                if (data?.granted_module) refreshProgression();
                playerRef.current?.refreshInventory?.();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Cannot loot this container');
              }
            })();
            return;
          }
          if (hud?.nearDerelictLog) {
            e.preventDefault?.();
            setDerelictOpen(true);
            return;
          }
          if (hud?.nearDerelictExit) {
            e.preventDefault?.();
            sceneRef.current?.exitDerelict?.();
            return;
          }
          return;
        }
        if (hud?.nearDerelict?.id) {
          e.preventDefault?.();
          setDerelictBoardedId(hud.nearDerelict.id);
          sceneRef.current?.enterDerelict?.();
          return;
        }
        // Priority: structure overlay > kiosk > anomaly > station dock > city overlay
        if (hud?.nearStructure && hud.nearStructure.doc) {
          e.preventDefault?.();
          setStructureOverlayDoc(hud.nearStructure.doc);
          return;
        }
        if (hud?.city?.nearKiosk && city) {
          e.preventDefault?.();
          setCityInitialTab(hud.city.nearKiosk.tab || 'lore');
          setCityOpen(true);
          return;
        }
        // Orbital station dock — disembark INTO the station interior.
        if (hud?.mode === 'space' && hud?.inShip && hud?.nearStation?.id) {
          e.preventDefault?.();
          if (typeof sceneRef.current?.enterStation === 'function') {
            sceneRef.current.enterStation(hud.nearStation);
          } else {
            // Engine not yet reloaded (no interior support) — fall back to the
            // trade overlay so docking still works.
            setDockedStation(hud.nearStation);
            setStationOpen(true);
          }
          return;
        }
        if (hud?.nearDiscovery?.id) {
          e.preventDefault?.();
          const { id, kind, label } = hud.nearDiscovery;
          (async () => {
            try {
              const { data } = await api.post('/api/discovery/claim', {
                coords_key: coords,
                planet_idx: Number(planetIdx),
                moon_idx: moonIdx != null ? Number(moonIdx) : null,
                kind,
                discovery_id: id,
                body_name: sceneRef.current?.body?.name || 'this world',
                body_type: sceneRef.current?.body?.type || 'world',
              });
              sceneRef.current?.markDiscoveryClaimed?.(id);
              if (data?.credits != null) updateCreditsRef.current?.(data.credits);
              if (data?.already_claimed) {
                toast(`${label} already collected`, { icon: '✅' });
              } else {
                const itemsTxt = (data?.items || [])
                  .map((it) => `${it.name}${it.qty > 1 ? ` ×${it.qty}` : ''}`).join(' · ');
                toast.success(
                  `${data?.rare ? '★ ' : ''}${label} · +${data?.reward || 0} cr${itemsTxt ? ` · ${itemsTxt}` : ''}`,
                );
                if (data?.lore) {
                  setDiscoveryLog({ label: data.label || label, kind, lore: data.lore, items: data.items || [] });
                }
                playerRef.current?.refreshInventory?.();
              }
            } catch (_e2) {
              toast.error('Could not collect');
            }
          })();
          return;
        }
        if (hud?.nearAnomaly?.id) {
          e.preventDefault?.();
          const id = hud.nearAnomaly.id;
          (async () => {
            try {
              const { data } = await api.post('/api/player/survey', {
                coords_key: coords,
                planet_idx: Number(planetIdx),
                anomaly_id: id,
              });
              sceneRef.current?.markAnomalyScanned?.(id);
              if (data?.credits != null) updateCreditsRef.current?.(data.credits);
              if (data?.already_scanned) {
                toast('Anomaly already catalogued · no bounty', { icon: '🛰️' });
              } else {
                toast.success(
                  `${data?.rare ? '★ Rare anomaly! ' : ''}Anomaly scanned · +${data?.reward ?? 0} cr`,
                );
              }
            } catch (_e) {
              toast.error('Scan failed');
            }
          })();
          return;
        }
        if (hud?.nearPed) {
          // Phase 13 — bark from the nearest pedestrian
          const ped = sceneRef.current?.getNearPedestrianFull?.();
          if (ped) {
            e.preventDefault?.();
            toast(`${ped.name}: "${ped.bark}"`, { duration: 5000 });
            return;
          }
        }
        if (hud?.city?.nearCity && city) {
          e.preventDefault?.();
          setCityInitialTab('lore');
          setCityOpen(true);
        }
      } else if (k === 'escape' && cityOpen) {
        e.preventDefault?.();
        setCityOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [city, cityOpen, api, coords, planetIdx, moonIdx]);

  // June 2026 — Hangar: load the player's active ship + catalog so the scene
  // builds the right hull. `activeShip` gates the scene mount effect; changing
  // it (after buying/selecting in the Hangar) rebuilds the scene with the new ship.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/ships/garage');
        if (cancelled) return;
        const ships = data?.ships || [];
        shipCatalogRef.current = ships;
        const id = data?.active_ship || 'scout';
        setActiveShip(id);
        setShipDef(ships.find((s) => s.id === id) || null);
      } catch (_e) {
        if (!cancelled) { setActiveShip('scout'); setShipDef(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // June 2026 / Phase 1 — push the player's COMBINED progression multipliers
  // (ship base × skills × installed modules) into the scene. The modules
  // loadout endpoint returns the authoritative recompute_derived_stats output.
  const refreshProgression = useCallback(async () => {
    try {
      const { data } = await api.get('/api/modules/loadout');
      const e = data?.effects || {};
      const prog = {
        walkMult: e.walk_mult || 1,
        shipSpeedMult: e.ship_speed_mult || 1,
        jumpMult: e.jump_mult || 1,
        miningMult: e.mining_mult || 1,
        scanRangeMult: e.scan_range_mult || 1,
        weaponDamageMult: e.weapon_damage_mult || 1,
        weaponFireRateMult: e.weapon_fire_rate_mult || 1,
      };
      setProgression(prog);
      sceneRef.current?.setProgression?.(prog);
    } catch (_e) { /* noop */ }
  }, [api]);

  useEffect(() => { refreshProgression(); }, [refreshProgression]);

  // Phase 11 — nanite catalog + balance + structures-on-body fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catRes, balRes] = await Promise.all([
          api.get('/api/nanites/catalog'),
          api.get('/api/nanites/balance'),
        ]);
        if (cancelled) return;
        setNaniteCatalog(catRes.data?.structures || []);
        setNaniteBalance(balRes.data?.nanites ?? 0);
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Fetch structures for the current body and push them into the scene.
  const refreshStructures = useCallback(async () => {
    if (!body || !coords || planetIdx == null) return;
    try {
      const { data } = await api.get('/api/structures', {
        params: {
          coords_key: coords,
          planet_idx: Number(planetIdx),
          moon_idx: moonIdx != null ? Number(moonIdx) : null,
        },
      });
      const list = data?.structures || [];
      setStructures(list);
      sceneRef.current?.setStructures?.(list);
    } catch (_e) { /* noop */ }
  }, [api, body, coords, planetIdx, moonIdx]);

  useEffect(() => { refreshStructures(); }, [refreshStructures]);

  // Re-push the fetched structures into the scene whenever it is (re)built
  // (e.g. the "Reload Engine" button bumps `sceneBuildKey`) or the list
  // changes. The scene-creation effect has no knowledge of the fetched
  // structures, so without this they vanish on every engine rebuild and can
  // also be lost to the initial scene-init race on a fresh page load.
  useEffect(() => {
    if (sceneRef.current && structures) {
      sceneRef.current.setStructures?.(structures);
    }
  }, [sceneBuildKey, structures]);

  // Phase 3 — Fetch derelict wrecks for this body (surface) + system (space).
  const refreshDerelicts = useCallback(async () => {
    if (!body || !coords || planetIdx == null) return;
    try {
      const [surf, spc] = await Promise.all([
        api.get('/api/derelicts/list', {
          params: {
            location: 'surface', coords_key: coords,
            planet_idx: Number(planetIdx),
            moon_idx: moonIdx != null ? Number(moonIdx) : null,
          },
        }),
        api.get('/api/derelicts/list', { params: { location: 'space', coords_key: coords } }),
      ]);
      setSurfaceDerelicts(surf?.data?.derelicts || []);
      setSpaceDerelicts(spc?.data?.derelicts || []);
    } catch (_e) { /* noop */ }
  }, [api, body, coords, planetIdx, moonIdx]);

  useEffect(() => { refreshDerelicts(); }, [refreshDerelicts]);

  // Push the context-appropriate wreck list into the scene. Re-runs on engine
  // rebuild (sceneBuildKey), when the lists change, or when the player flips
  // between the surface and space views.
  useEffect(() => {
    if (!sceneRef.current) return;
    const inSpace = hudState.mode === 'space';
    sceneRef.current.setDerelicts?.(inSpace ? spaceDerelicts : surfaceDerelicts, inSpace ? 'space' : 'surface');
  }, [sceneBuildKey, surfaceDerelicts, spaceDerelicts, hudState.mode]);


  // B key toggles build menu; LMB while preview-active commits
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'b') {
        e.preventDefault?.();
        if (sceneRef.current?.state?.inShip) {
          toast('Exit the ship to build structures');
          return;
        }
        setBuildMenuOpen((open) => !open);
      } else if (k === 'escape' && buildState.active) {
        e.preventDefault?.();
        sceneRef.current?.stopBuildPreview?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [buildState.active]);

  // LMB while build preview active = commit; bind on the canvas only.
  useEffect(() => {
    const handler = async (e) => {
      if (e.button !== 0) return;
      if (!sceneRef.current) return;
      const bs = sceneRef.current.getState?.()?.build;
      if (!bs?.active) return;
      e.preventDefault?.();
      e.stopPropagation?.();
      const payload = sceneRef.current.commitBuild?.();
      if (!payload) return;
      try {
        const { data } = await api.post('/api/structures/build', {
          ...payload,
          coords_key: coords,
          planet_idx: Number(planetIdx),
          moon_idx: moonIdx != null ? Number(moonIdx) : null,
        });
        toast.success(`Built ${data?.type || 'structure'}`);
        sceneRef.current.upsertStructure?.(data);
        setStructures((prev) => [...prev, data]);
        // Refresh nanite balance
        const balRes = await api.get('/api/nanites/balance');
        setNaniteBalance(balRes.data?.nanites ?? 0);
        sceneRef.current.stopBuildPreview?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Build failed');
      }
    };
    const canvas = canvasRef.current;
    if (canvas) canvas.addEventListener('mousedown', handler);
    return () => { if (canvas) canvas.removeEventListener('mousedown', handler); };
  }, [api, coords, planetIdx, moonIdx]);

  // Refresh credit/inventory/state + nanite balance after structure actions
  const handleStructureAction = useCallback(async (kind, payload) => {
    try {
      const [s, i, bal] = await Promise.all([
        api.get('/api/player/state').then((r) => r.data),
        api.get('/api/player/inventory').then((r) => r.data),
        api.get('/api/nanites/balance').then((r) => r.data),
      ]);
      if (s) sceneRef.current?.setPlayerVitals?.({
        hp: s.hp, maxHp: s.max_hp, shield: s.shield, maxShield: s.max_shield,
      });
      void i;
      if (bal?.nanites != null) setNaniteBalance(bal.nanites);
      playerRef.current?.refreshState?.();
      playerRef.current?.refreshInventory?.();
      if (kind === 'recycle' && payload?.id) {
        sceneRef.current?.removeStructure?.(payload.id);
        setStructures((prev) => prev.filter((st) => st.id !== payload.id));
      }
    } catch (_e) { /* noop */ }
  }, [api]);

  // Sync buildState from scene
  useEffect(() => {
    const id = setInterval(() => {
      const b = sceneRef.current?.getState?.()?.build;
      if (b) setBuildState((prev) => (
        prev.active === b.active && prev.typeId === b.typeId && prev.valid === b.valid
          ? prev : { ...b }
      ));
    }, 120);
    return () => clearInterval(id);
  }, []);

  // Pause GameScene input while an overlay is open
  useEffect(() => {
    sceneRef.current?.setInputPaused?.(
      cityOpen || inventoryOpen || settingsOpen || buildMenuOpen || !!structureOverlayDoc
      || stationOpen || hangarOpen || modulesOpen || skillTreeOpen || craftingOpen
    );
  }, [cityOpen, inventoryOpen, settingsOpen, buildMenuOpen, structureOverlayDoc, stationOpen, hangarOpen, modulesOpen, skillTreeOpen, craftingOpen]);

  // Refresh persistent vitals after city actions (refuel/buy) so HUD reflects them
  const handleAfterCityAction = useCallback(async (kind) => {
    try {
      const [s, i] = await Promise.all([
        api.get('/api/player/state').then((r) => r.data),
        api.get('/api/player/inventory').then((r) => r.data),
      ]);
      // Push vitals into the scene so HUD / combat updates immediately
      sceneRef.current?.setPlayerVitals?.({
        hp: s?.hp, maxHp: s?.max_hp,
        shield: s?.shield, maxShield: s?.max_shield,
      });
      playerRef.current?.refreshState?.();
      playerRef.current?.refreshInventory?.();
      // ignore lint about unused kind
      void kind;
      void i;
    } catch (_e) { /* noop */ }
  }, [api]);

  // Phase 8 — G key sells all cargo when standing at the ship dock
  useEffect(() => {
    const onKey = async (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      if (k !== 'g') return;
      // Must be on foot near the ship with cargo > 0
      const cargoUsed = (playerRef.current?.inventory?.ores || []).reduce((a, o) => a + (o.qty || 0), 0);
      const hud = sceneRef.current?.getState?.();
      if (!hud) return;
      if (hud.inShip) return;
      if (!hud.nearShip) return;
      if (cargoUsed <= 0) return;
      try {
        // Player & ship now have distinct inventories. The G hotkey stows the
        // player's backpack into the ship's cargo hold. Selling only happens
        // at a city kiosk.
        const { data } = await api.post('/api/ship/stow', {});
        await playerRef.current?.refreshInventory?.();
        toast.success(`Stowed ${Math.round(data?.total_moved || 0)} u in ship cargo`);
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Stow failed';
        toast.error(detail);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [api, updateCredits]);

  // Phase 6 — autosave last-body position every time the scene emits;
  // the debounced flush in usePlayerState handles backend writes.
  useEffect(() => {
    if (!hudState?.coords || !body) return;
    player.queueStatePatch?.({
      last_body: {
        coords_key: body.coords || coords,
        planet_idx: body.currentPlanetIdx ?? Number(planetIdx),
        moon_idx: body.currentMoonIdx ?? (moonIdx !== undefined ? Number(moonIdx) : null),
        pos: hudState.coords,
        yaw: ((hudState.heading || 0) * Math.PI) / 180,
      },
    });
    // We intentionally only patch when coords change meaningfully; usePlayerState
    // debounces actual HTTP writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    Math.round(hudState.coords.x / 5),
    Math.round(hudState.coords.z / 5),
  ]);

  const handleExit = useCallback(() => {
    const backTo = location.state?.from || `/system/${coords}`;
    navigate(backTo);
  }, [navigate, location.state, coords]);

  const handleToggleCamera = useCallback(() => {
    sceneRef.current?.toggleCameraMode();
  }, []);

  const handleToggleFlashlight = useCallback(() => {
    sceneRef.current?.toggleFlashlight();
  }, []);

  const handleToggleShip = useCallback(() => {
    sceneRef.current?.toggleShip();
  }, []);

  const handleLaunchToOrbit = useCallback(() => {
    sceneRef.current?.enterSpaceMode();
  }, []);

  const handleLand = useCallback(() => {
    sceneRef.current?.landOnTarget();
  }, []);

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black overflow-hidden"
      data-testid="explore-3d-root"
      style={{ fontFamily: 'Fira Sans, sans-serif' }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full block outline-none"
        data-testid="game-canvas"
        tabIndex={0}
      />

      {/* June 2026 — underwater tint + vignette while the camera is submerged */}
      {hudState.underwater && (
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          data-testid="underwater-overlay"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(10,60,80,0.18) 0%, rgba(4,30,44,0.45) 78%, rgba(2,18,30,0.62) 100%)',
            mixBlendMode: 'multiply',
          }}
        />
      )}

      {/* Loading state */}
      {loading && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-[#020610]/95 z-30"
          data-testid="explore-loading"
        >
          <div className="text-cyan-300 text-sm tracking-[0.3em] uppercase mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Initializing Surface Drop
          </div>
          <div className="flex space-x-2">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.15s' }} />
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.3s' }} />
          </div>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-[#020610]/95 z-30 p-6 text-center"
          data-testid="explore-error"
        >
          <h2 className="text-2xl text-red-300 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Drop Aborted
          </h2>
          <p className="text-slate-300 text-sm max-w-md mb-6">{error}</p>
          <button
            onClick={handleExit}
            className="px-5 py-2.5 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg transition-colors"
            data-testid="error-exit-button"
          >
            Return to System
          </button>
        </div>
      )}

      {/* HUD overlay (only when scene is up) */}
      {!loading && !error && body && (
        <>
          {/* Top-left: body title card (compact — atmosphere/gravity now in AtmospherePanel) */}
          <div
            className="pointer-events-none absolute top-4 left-4 px-4 py-3 rounded-xl border border-cyan-400/25 bg-[#02060f]/70 backdrop-blur-md shadow-[0_0_0_1px_rgba(24,210,255,0.15)] z-20 min-w-[220px]"
            data-testid="hud-body-info"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/80" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {body.kind === 'moon' ? `Moon · Orbiting ${body.parentPlanet}` : `${body.type} Body`}
            </div>
            <div className="text-lg leading-tight text-slate-100 mt-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600 }}>
              {body.name}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-300/85" style={{ fontFamily: 'Source Code Pro, monospace' }}>
              <span>HAB: {body.habitability ?? 0}/100</span>
              {body.star?.class && <span>STAR: {body.star.class}</span>}
            </div>
            {(!body.scannedElements || body.scannedElements.length === 0) ? (
              <div
                className="mt-2 px-2 py-1 rounded border border-amber-400/30 bg-amber-500/5 text-[10px] tracking-[0.2em] uppercase text-amber-300/90"
                data-testid="hud-unscanned-notice"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Unscanned · No detectable deposits
                <div className="mt-0.5 text-[9px] tracking-normal text-slate-400/85 normal-case">
                  Scan this body from orbit to reveal what can be mined.
                </div>
              </div>
            ) : (
              <div
                className="mt-2 px-2 py-1 rounded border border-emerald-400/30 bg-emerald-500/5 text-[10px] tracking-[0.2em] uppercase text-emerald-300/90"
                data-testid="hud-scanned-summary"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                {body.scannedElements.length} element{body.scannedElements.length === 1 ? '' : 's'} detected
              </div>
            )}
          </div>

          {/* Top-left: coordinate readout (under the body card) */}
          <div className="pointer-events-none absolute top-[155px] left-4 z-20" data-testid="hud-coords-wrap">
            <div className="px-3 py-1.5 rounded-md border border-cyan-400/25 bg-[#02060f]/60 backdrop-blur text-[10px] text-cyan-100/90 tracking-[0.1em]"
              style={{ fontFamily: 'Source Code Pro, monospace' }}
              data-testid="hud-coords"
            >
              <span className="text-cyan-300/70">X</span> {Math.round(hudState.coords?.x || 0)}{' '}
              <span className="text-cyan-300/70 ml-1">Y</span> {Math.round(hudState.coords?.y || 0)}{' '}
              <span className="text-cyan-300/70 ml-1">Z</span> {Math.round(hudState.coords?.z || 0)}
            </div>
          </div>

          {/* Top-center: compass strip */}
          <CompassStrip
            heading={hudState.heading || 0}
            shipBearing={hudState.shipBearing || 0}
            shipDistance={hudState.shipDistance || 0}
            pois={[]}
          />

          {/* Top-right: help/settings/exit buttons */}
          <div className="absolute top-4 right-4 z-20 flex gap-2">
            <button
              onClick={() => {
                // Force a full page reload so the latest engine bundle is
                // picked up. Stateful position is rebuilt from the URL/coords
                // so this is safe to do mid-session.
                try { window.location.reload(); } catch (_e) {
                  // Fallback: at least dispose + re-create the GameScene
                  setSceneBuildKey((k) => k + 1);
                }
              }}
              className="px-3 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-white/5 backdrop-blur border border-amber-400/25 text-amber-200 hover:bg-white/10 transition-colors"
              data-testid="rebuild-scene-button"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              title="Force-reload the engine and game bundle"
            >
              Reload Engine
            </button>
            <button
              onClick={() => setHelpOpen(true)}
              className="px-3 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-white/5 backdrop-blur border border-cyan-400/25 text-cyan-200 hover:bg-white/10 transition-colors"
              data-testid="toggle-help-button"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              title="Open keyboard reference (F1)"
            >
              F1 · Help
            </button>
            <button
              onClick={() => setSettingsOpen(true)}
              className="px-3 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-white/5 backdrop-blur border border-slate-400/25 text-slate-200 hover:bg-white/10 transition-colors"
              data-testid="hud-settings-button"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              aria-label="Open settings"
            >
              Settings
            </button>
            <button
              onClick={handleExit}
              className="px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold transition-colors shadow-[0_0_24px_rgba(24,210,255,0.25)]"
              data-testid="exit-3d-button"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Exit to System
            </button>
          </div>

          {/* Right side: inventory mini-panel + atmosphere/O2 stack */}
          <div className="absolute top-[68px] right-4 z-20 flex flex-col gap-3 max-w-[240px]" data-testid="hud-right-stack">
            <InventoryMiniPanel
              credits={user?.credits ?? 0}
              inventory={player.inventory}
              onOpen={() => setInventoryOpen(true)}
            />
            <AtmospherePanel
              atmosphere={body.atmosphere || 'None'}
              gravity={hudState.gravity || 0}
              oxygen={hudState.oxygen ?? 100}
              maxOxygen={hudState.maxOxygen ?? 100}
              oxygenDepleting={hudState.oxygenDepleting}
              breathable={hudState.breathable !== false}
              biomeLabel={hudState.biomeLabel || (body.kind === 'moon' ? `Moon · ${body.parentType || ''}` : body.type)}
              bodyType={body.kind === 'moon' ? 'Moon' : (body.type || 'Default')}
              habitability={body.habitability ?? 0}
              weather={hudState.weather || null}
            />
          </div>

          {/* Phase 11 — Nanite balance (bottom-left so it doesn't fight the
              cargo/credits panel on the right or the planet info top-left). */}
          {!hudState.inShip && (
            <div
              className="absolute bottom-4 left-4 z-20 select-none"
              data-testid="hud-nanite-badge"
            >
              <div
                className="px-3 py-1.5 rounded-md border border-amber-300/30 bg-[#120e05]/85 backdrop-blur text-amber-200 text-xs tracking-[0.15em] uppercase flex items-center gap-2"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <span className="text-amber-300">◆</span>
                <span className="tabular-nums">{naniteBalance}</span>
                <span className="text-slate-400">nanites</span>
                <span className="text-slate-500 ml-1 text-[10px]">· Press B</span>
              </div>
            </div>
          )}

          {/* Bottom-right: mini-radar */}
          <MiniRadar
            heading={hudState.heading || 0}
            highlightKinds={[
              ...((hudState.anomalyPois?.length ?? 0) > 0 ? ['anomaly'] : []),
              ...((hudState.discoveryPois?.length ?? 0) > 0 ? ['discovery'] : []),
            ]}
            pois={[
              ...(hudState.pois || []).map((p) => ({
                id: p.id,
                kind: p.kind,
                dx: p.x - (hudState.coords?.x || 0),
                dz: p.z - (hudState.coords?.z || 0),
              })),
              ...((hudState.enemyPois || []).map((p) => ({
                id: p.id,
                kind: p.kind,
                dx: p.x - (hudState.coords?.x || 0),
                dz: p.z - (hudState.coords?.z || 0),
              }))),
              ...((hudState.depositPois || []).map((p) => ({
                id: p.id,
                kind: p.kind,
                dx: p.x - (hudState.coords?.x || 0),
                dz: p.z - (hudState.coords?.z || 0),
              }))),
              ...((hudState.anomalyPois || []).map((p) => ({
                id: p.id,
                kind: p.kind,
                dx: p.x - (hudState.coords?.x || 0),
                dz: p.z - (hudState.coords?.z || 0),
              }))),
              ...((hudState.discoveryPois || []).map((p) => ({
                id: p.id,
                kind: p.kind,
                dx: p.x - (hudState.coords?.x || 0),
                dz: p.z - (hudState.coords?.z || 0),
              }))),
              ...((!hudState.inCave ? (hudState.cavePois || []) : []).map((p) => ({
                id: p.id,
                kind: p.kind,
                dx: p.x - (hudState.coords?.x || 0),
                dz: p.z - (hudState.coords?.z || 0),
              }))),
              ...((!hudState.inDerelict ? (hudState.derelictPois || []) : []).map((p) => ({
                id: p.id,
                kind: p.kind,
                dx: p.x - (hudState.coords?.x || 0),
                dz: p.z - (hudState.coords?.z || 0),
              }))),
            ]}
            radiusMeters={200}
          />

          {/* Vitals strip — HP / Shield / Ammo */}
          <VitalsStrip
            hp={player.state?.hp ?? 100}
            maxHp={player.state?.max_hp ?? 100}
            shield={player.state?.shield ?? 500}
            maxShield={player.state?.max_shield ?? 500}
            ammo={hudState.weapon?.ammoCurrent ?? null}
            maxAmmo={hudState.weapon?.ammoMax ?? null}
            reloading={hudState.weapon?.reloading || false}
            reloadProgress={hudState.weapon?.reloadProgress || 0}
            weaponName={hudState.weapon?.weaponName || null}
          />

          {/* Bottom-left: HUD controls panel */}
          {/* Bottom-center: camera mode + ship prompt */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3" data-testid="hud-bottom-bar">
            {!hudState.inShip && (
              <button
                onClick={handleToggleCamera}
                className="px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-white/5 backdrop-blur border border-cyan-400/25 text-cyan-100 hover:bg-white/10 transition-colors"
                data-testid="toggle-camera-button"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Camera: {hudState.cameraMode === 'fps' ? 'First-Person' : 'Third-Person'}
              </button>
            )}

            {!hudState.inShip && (
              <button
                onClick={handleToggleFlashlight}
                className={`px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-lg backdrop-blur border transition-colors ${
                  hudState.flashlightOn
                    ? 'bg-amber-300/20 border-amber-300/50 text-amber-100'
                    : 'bg-white/5 border-cyan-400/25 text-cyan-100 hover:bg-white/10'
                }`}
                data-testid="toggle-flashlight-button"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Flashlight: {hudState.flashlightOn ? 'On' : 'Off'} (L)
              </button>
            )}

            {hudState.inShip && (
              <button
                onClick={handleToggleCamera}
                className="px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-white/5 backdrop-blur border border-cyan-400/25 text-cyan-100 hover:bg-white/10 transition-colors"
                data-testid="toggle-ship-camera-button"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                title="Press V to toggle cockpit / chase camera"
              >
                View: {hudState.shipCameraMode === 'fps' ? 'Cockpit' : 'Chase'}
              </button>
            )}

            {hudState.nearShip && !hudState.inShip && (
              <button
                onClick={handleToggleShip}
                className="px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold transition-colors shadow-[0_0_28px_rgba(24,210,255,0.45)] animate-pulse"
                data-testid="enter-ship-button"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                ▸ Press E to Enter Ship
              </button>
            )}

            {hudState.inShip && hudState.mode !== 'space' && (
              <button
                onClick={handleToggleShip}
                disabled={!hudState.canExit}
                className={`px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-lg transition-colors font-semibold ${
                  hudState.canExit
                    ? 'bg-amber-400/90 hover:bg-amber-300 text-slate-900'
                    : 'bg-white/5 border border-amber-400/30 text-amber-200/70 cursor-not-allowed'
                }`}
                data-testid="exit-ship-button"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                title={hudState.canExit ? 'Press E to disembark' : 'Slow down and land to disembark'}
              >
                {hudState.canExit ? '▸ Press E to Disembark' : '◌ Land to Disembark'}
              </button>
            )}

            {hudState.inShip && hudState.mode !== 'space' && hudState.canLaunch && (
              <button
                onClick={handleLaunchToOrbit}
                className="px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold transition-colors shadow-[0_0_28px_rgba(24,210,255,0.5)] animate-pulse"
                data-testid="launch-orbit-button"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                ▲ Launch to Orbit
              </button>
            )}

            {hudState.mode === 'space' && hudState.approachTarget && (
              <button
                onClick={handleLand}
                className="px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-lg bg-emerald-400/90 hover:bg-emerald-300 text-slate-900 font-semibold transition-colors shadow-[0_0_28px_rgba(52,211,153,0.55)] animate-pulse"
                data-testid="land-button"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                ▼ Press E to Land on {hudState.approachTarget.name}
              </button>
            )}

            {hudState.inShip && hudState.mode === 'space' && !hudState.approachTarget && (
              <div
                className="px-3 py-2 text-[10px] tracking-[0.3em] uppercase rounded-lg bg-[#02060f]/80 border border-rose-400/30 text-rose-200/90"
                data-testid="ship-laser-hint"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                title="Twin pulse lasers: F or left-click"
              >
                ◎ Lasers · F / Click
              </div>
            )}
          </div>

          {/* Flight HUD (when piloting) */}
          {hudState.inShip && (
            <div
              className="pointer-events-none absolute inset-0 z-10"
              data-testid="flight-hud"
            >
              {/* Cockpit frame */}
              <div className="absolute inset-0 border-[10px] border-[#0a1726]/85 rounded-[32px] shadow-[inset_0_0_140px_rgba(0,0,0,0.7)]" />

              {/* Top-center: cockpit banner */}
              <div className="absolute top-6 left-1/2 -translate-x-1/2 px-5 py-2 rounded-md bg-[#02060f]/85 border border-cyan-400/30 text-cyan-200 text-[10px] tracking-[0.4em] uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {hudState.mode === 'space'
                  ? (hudState.approachTarget
                      ? `Cockpit · Approaching ${hudState.approachTarget.name}`
                      : (hudState.shipBoost ? 'Cockpit · Afterburner Engaged · System Space' : 'Cockpit · Drifting · System Space'))
                  : (hudState.shipBoost ? 'Cockpit · Afterburner Engaged' : hudState.canExit ? 'Cockpit · Systems Online · Landed' : 'Cockpit · Systems Online · In Flight')}
              </div>

              {/* Bottom-right: speed + altitude (or nearest body in space).
                  Sits ABOVE the MiniRadar so the two never overlap. */}
              <div className="absolute bottom-[244px] right-4 grid gap-2 justify-items-end text-cyan-200/90" style={{ fontFamily: 'Source Code Pro, monospace' }}>
                <div className="px-4 py-2 border border-cyan-400/30 rounded bg-[#02060f]/80 min-w-[140px]">
                  <div className="text-[9px] tracking-[0.3em] uppercase text-cyan-300/70">Speed</div>
                  <div className="text-2xl text-cyan-100 tabular-nums">{Math.round(hudState.shipSpeed)}<span className="text-[10px] text-cyan-300/70 ml-1">m/s</span></div>
                </div>
                {hudState.mode === 'space' ? (
                  <div className="px-4 py-2 border border-cyan-400/30 rounded bg-[#02060f]/80 min-w-[180px]">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-cyan-300/70">Nearest Body</div>
                    <div className="text-sm text-cyan-100 truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{hudState.nearestBodyName || '—'}</div>
                    <div className="text-[11px] tabular-nums text-cyan-300/80">{Math.round(hudState.nearestBodyDistance)} u</div>
                  </div>
                ) : (
                  <div className="px-4 py-2 border border-cyan-400/30 rounded bg-[#02060f]/80 min-w-[140px]">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-cyan-300/70">Altitude</div>
                    <div className="text-2xl text-cyan-100 tabular-nums">{Math.round(hudState.shipAltitude)}<span className="text-[10px] text-cyan-300/70 ml-1">m</span></div>
                  </div>
                )}
              </div>

              {/* Bottom-left: throttle gauge */}
              <div className="absolute bottom-24 left-8 px-4 py-3 border border-cyan-400/30 rounded bg-[#02060f]/80 min-w-[180px]">
                <div className="text-[9px] tracking-[0.3em] uppercase text-cyan-300/70 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Throttle</div>
                <div className="h-2 w-full bg-cyan-900/40 rounded overflow-hidden">
                  <div
                    className={`h-full transition-[width] duration-100 ${hudState.shipBoost ? 'bg-amber-400' : 'bg-cyan-400'}`}
                    style={{ width: `${Math.max(0, Math.min(1, hudState.shipThrottle)) * 100}%` }}
                  />
                </div>
                <div className="mt-2 flex gap-2 text-[10px]" style={{ fontFamily: 'Source Code Pro, monospace' }}>
                  <span className={`px-2 py-0.5 rounded border ${hudState.shipBoost ? 'border-amber-400/60 text-amber-200' : 'border-cyan-400/30 text-cyan-300/70'}`}>BOOST</span>
                  {hudState.mode === 'space' ? (
                    <span className="px-2 py-0.5 rounded border border-cyan-400/30 text-cyan-300/70">VACUUM</span>
                  ) : (
                    <span className={`px-2 py-0.5 rounded border ${hudState.canExit ? 'border-emerald-400/50 text-emerald-200' : 'border-cyan-400/30 text-cyan-300/70'}`}>LANDED</span>
                  )}
                </div>
              </div>

              {/* Center reticle while piloting */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="w-3 h-3 border border-cyan-300/70 rounded-full" />
              </div>

              {/* Exit-denied flash */}
              {hudState.exitDenied && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-12 px-4 py-2 rounded-md bg-red-500/20 border border-red-400/60 text-red-100 text-[11px] tracking-[0.3em] uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Land &amp; come to a stop to disembark
                </div>
              )}
            </div>
          )}

          {/* Pointer-lock reticle (only when on foot) */}
          {!hudState.inShip && (
            <div
              className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
              data-testid="reticle"
            >
              <div
                className={`w-1.5 h-1.5 rounded-full ${
                  hudState.weapon?.reloading ? 'bg-amber-300/80' : 'bg-cyan-300/80'
                } shadow-[0_0_8px_rgba(24,210,255,0.9)]`}
              />
            </div>
          )}

          {/* Combat damage vignette (red flash on hit) */}
          <DamageVignette
            lastDamageAt={hudState.lastDamageAt || 0}
            hp={player.state?.hp ?? 100}
          />

          {/* June 2026 — lightning flash on storm strikes */}
          <LightningFlash at={hudState.lightningAt || 0} />

          {/* Phase 8 — mining HUD widgets */}
          <MiningPrompt mining={hudState.mining || null} />
          <MiningProgress mining={hudState.mining || null} />
          {(() => {
            const cargoUsed = (player.inventory?.ores || []).reduce((a, o) => a + (o.qty || 0), 0);
            const shipUsed = (player.inventory?.ship_cargo || []).reduce((a, o) => a + (o.qty || 0), 0);
            const showStow = !hudState.inShip && hudState.nearShip && (cargoUsed > 0 || shipUsed > 0) && !deathSummary;
            return (
              <ShipDockPrompt
                visible={showStow}
                totalUnits={cargoUsed}
                shipCargoUnits={shipUsed}
                onStow={async () => {
                  try {
                    const { data } = await api.post('/api/ship/stow', {});
                    await playerRef.current?.refreshInventory?.();
                    toast.success(`Stowed ${Math.round(data?.total_moved || 0)} u in ship cargo`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Stow failed');
                  }
                }}
                onRetrieve={async () => {
                  try {
                    const { data } = await api.post('/api/ship/retrieve', {});
                    await playerRef.current?.refreshInventory?.();
                    toast.success(`Retrieved ${Math.round(data?.total_moved || 0)} u from ship`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Retrieve failed');
                  }
                }}
              />
            );
          })()}

          {/* Phase 9 — City entry prompt (only on foot, only when in plaza) */}
          <CityEnterPrompt
            visible={
              !hudState.inShip
              && !!city
              && !!hudState.city?.nearCity
              && !hudState.city?.nearKiosk
              && !deathSummary
              && !cityOpen
            }
            cityName={city?.name || hudState.city?.name || 'Colony'}
            onEnter={() => { setCityInitialTab('lore'); setCityOpen(true); }}
          />

          {/* Orbital station dock prompt — only while flying in space mode */}
          {hudState.mode === 'space' && hudState.inShip && hudState.nearStation && !stationOpen && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-station-dock-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-amber-400/40 bg-[#1a1206]/90 backdrop-blur shadow-[0_0_28px_rgba(251,191,36,0.25)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90 text-center">
                  Trading Station
                </div>
                <div className="mt-0.5 text-sm text-amber-100 text-center" data-testid="hud-station-prompt-name">
                  {hudState.nearStation.name}
                </div>
                <button
                  onClick={() => {
                    if (typeof sceneRef.current?.enterStation === 'function') {
                      sceneRef.current.enterStation(hudState.nearStation);
                    } else {
                      setDockedStation(hudState.nearStation);
                      setStationOpen(true);
                    }
                  }}
                  className="mt-2 w-full px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-amber-400/90 hover:bg-amber-300 text-slate-900 font-semibold transition-colors"
                  data-testid="hud-station-dock-button"
                >
                  Press E · Dock
                </button>
              </div>
            </div>
          )}

          {/* June 2026 — Station interior: trade terminal prompt */}
          {hudState.inStation && !!hudState.nearStationTerminal && !stationOpen && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-station-terminal-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-cyan-400/50 bg-[#06141f]/90 backdrop-blur shadow-[0_0_28px_rgba(34,211,238,0.3)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/90 text-center">
                  {hudState.stationInfo?.name || 'Station'}
                </div>
                <button
                  onClick={() => { setDockedStation(hudState.stationInfo); setStationOpen(true); }}
                  className="mt-2 w-full px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-cyan-400/90 hover:bg-cyan-300 text-slate-900 font-semibold transition-colors"
                  data-testid="hud-station-terminal-button"
                >
                  Press E · Trade Terminal
                </button>
              </div>
            </div>
          )}

          {/* June 2026 — Station interior: Shipyard / Hangar prompt */}
          {hudState.inStation && !!hudState.nearStationShipyard && !stationOpen && !hangarOpen && !modulesOpen && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-station-shipyard-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-fuchsia-400/50 bg-[#150a1f]/90 backdrop-blur shadow-[0_0_28px_rgba(217,70,239,0.3)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-fuchsia-300/90 text-center">
                  Shipyard
                </div>
                <button
                  onClick={() => setHangarOpen(true)}
                  className="mt-2 w-full px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-fuchsia-500/90 hover:bg-fuchsia-400 text-white font-semibold transition-colors"
                  data-testid="hud-station-shipyard-button"
                >
                  Press E · Open Hangar
                </button>
              </div>
            </div>
          )}

          {/* June 2026 — Station interior: Neural Console / Skill Tree prompt */}
          {hudState.inStation && !!hudState.nearStationSkills && !stationOpen && !skillTreeOpen && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-station-skills-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-teal-400/50 bg-[#06140f]/90 backdrop-blur shadow-[0_0_28px_rgba(45,212,191,0.3)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-teal-300/90 text-center">
                  Neural Console
                </div>
                <button
                  onClick={() => setSkillTreeOpen(true)}
                  className="mt-2 w-full px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-teal-500/90 hover:bg-teal-400 text-slate-900 font-semibold transition-colors"
                  data-testid="hud-station-skills-button"
                >
                  Press E · Skill Tree
                </button>
              </div>
            </div>
          )}

          {/* June 2026 — Station interior: airlock / return-to-ship prompt */}
          {hudState.inStation && !!hudState.nearStationExit && !hudState.nearStationTerminal && !hudState.nearStationShipyard && !hudState.nearStationSkills && !stationOpen && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-station-exit-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-emerald-400/50 bg-[#04150c]/90 backdrop-blur shadow-[0_0_24px_rgba(52,211,153,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300/90 text-center">
                  Airlock
                </div>
                <div className="mt-0.5 text-sm text-emerald-100 text-center">
                  Press E · Board Ship
                </div>
              </div>
            </div>
          )}

          {/* June 2026 — Ship interior: board cabin prompt (large landed ships) */}
          {hudState.canBoardInterior && !hudState.inShipInterior && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-board-cabin-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-cyan-400/50 bg-[#06141f]/90 backdrop-blur shadow-[0_0_28px_rgba(34,211,238,0.3)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/90 text-center">
                  {hudState.shipName || 'Ship'} · Cabin
                </div>
                <div className="mt-0.5 text-sm text-cyan-100 text-center">
                  Press G · Board Cabin
                </div>
              </div>
            </div>
          )}

          {/* Phase 2 — Ship interior: Fabricator terminal prompt */}
          {hudState.inShipInterior && !!hudState.nearShipFabricator && (
            <div
              className="absolute bottom-56 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-ship-fabricator-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-amber-400/50 bg-[#1a1206]/90 backdrop-blur shadow-[0_0_24px_rgba(245,180,60,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90 text-center">
                  Fabricator
                </div>
                <div className="mt-0.5 text-sm text-amber-100 text-center">
                  Press E · Refine Alloys
                </div>
              </div>
            </div>
          )}

          {/* June 2026 — Ship interior: exit-hatch / return-to-cockpit prompt */}
          {hudState.inShipInterior && !!hudState.nearShipInteriorExit && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-cabin-exit-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-emerald-400/50 bg-[#04150c]/90 backdrop-blur shadow-[0_0_24px_rgba(52,211,153,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300/90 text-center">
                  Exit Hatch
                </div>
                <div className="mt-0.5 text-sm text-emerald-100 text-center">
                  Press E · Return to Cockpit
                </div>
              </div>
            </div>
          )}


          {/* Phase 12c — Hub kiosk prompt (shop / missions / refuel) */}
          {!hudState.inShip && !!city && hudState.city?.nearKiosk && !deathSummary && !cityOpen && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-kiosk-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-cyan-400/40 bg-[#06121f]/90 backdrop-blur shadow-[0_0_28px_rgba(24,210,255,0.25)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/90 text-center">
                  Kiosk
                </div>
                <div className="mt-0.5 text-sm text-cyan-100 text-center" data-testid="hud-kiosk-prompt-label">
                  {hudState.city.nearKiosk.label}
                </div>
                <button
                  onClick={() => {
                    setCityInitialTab(hudState.city.nearKiosk.tab || 'lore');
                    setCityOpen(true);
                  }}
                  className="mt-2 w-full px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-cyan-400/90 hover:bg-cyan-300 text-slate-900 font-semibold transition-colors"
                  data-testid="hud-kiosk-interact-button"
                >
                  Press E · Use
                </button>
              </div>
            </div>
          )}

          {/* June 2026 — Cave entrance prompt (surface) */}
          {!hudState.inShip && !hudState.inCave && !!hudState.nearCaveEntrance && !deathSummary
            && !cityOpen && !hudState.city?.nearKiosk && !hudState.nearStructure && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-cave-enter-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-violet-400/50 bg-[#0c0618]/90 backdrop-blur shadow-[0_0_24px_rgba(167,139,250,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-violet-300/90 text-center">
                  Cave Entrance
                </div>
                <div className="mt-0.5 text-sm text-violet-100 text-center">
                  Press E · Enter Cave
                </div>
              </div>
            </div>
          )}

          {/* June 2026 — Cave exit prompt (underground) */}
          {!hudState.inShip && hudState.inCave && !!hudState.nearCaveExit && !deathSummary && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-cave-exit-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-emerald-400/50 bg-[#04150c]/90 backdrop-blur shadow-[0_0_24px_rgba(52,211,153,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300/90 text-center">
                  Surface Exit
                </div>
                <div className="mt-0.5 text-sm text-emerald-100 text-center">
                  Press E · Climb Out
                </div>
              </div>
            </div>
          )}

          {/* Phase 3 — Board wreck prompt (surface on foot OR piloting in space) */}
          {!hudState.inDerelict && !!hudState.nearDerelict && !deathSummary
            && !cityOpen && !hudState.city?.nearKiosk && !hudState.nearStructure && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-derelict-board-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-amber-400/50 bg-[#1a0f04]/90 backdrop-blur shadow-[0_0_24px_rgba(251,146,60,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90 text-center">
                  Derelict · {hudState.nearDerelict.name}
                </div>
                <div className="mt-0.5 text-sm text-amber-100 text-center">
                  Press E · Board Wreck
                </div>
              </div>
            </div>
          )}

          {/* Phase 3 — Aboard a wreck: container / log / exit prompts */}
          {hudState.inDerelict && !!hudState.nearDerelictContainer && !deathSummary && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-derelict-loot-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-amber-400/50 bg-[#1a0f04]/90 backdrop-blur shadow-[0_0_24px_rgba(251,146,60,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90 text-center">
                  {hudState.nearDerelictContainer.label}
                </div>
                <div className="mt-0.5 text-sm text-amber-100 text-center">
                  Press E · Loot
                </div>
              </div>
            </div>
          )}
          {hudState.inDerelict && !hudState.nearDerelictContainer && !!hudState.nearDerelictLog && !deathSummary && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-derelict-log-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-cyan-400/50 bg-[#04141a]/90 backdrop-blur shadow-[0_0_24px_rgba(34,211,238,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/90 text-center">
                  Ship&apos;s Log Terminal
                </div>
                <div className="mt-0.5 text-sm text-cyan-100 text-center">
                  Press E · Read Log
                </div>
              </div>
            </div>
          )}
          {hudState.inDerelict && !hudState.nearDerelictContainer && !hudState.nearDerelictLog && !!hudState.nearDerelictExit && !deathSummary && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-derelict-exit-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-emerald-400/50 bg-[#04150c]/90 backdrop-blur shadow-[0_0_24px_rgba(52,211,153,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300/90 text-center">
                  Airlock
                </div>
                <div className="mt-0.5 text-sm text-emerald-100 text-center">
                  Press E · Leave Wreck
                </div>
              </div>
            </div>
          )}

          {/* Phase 13 (P3) — Anomaly scan prompt */}
          {!hudState.inShip && !!hudState.nearAnomaly && !deathSummary && !cityOpen && !hudState.city?.nearKiosk && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-anomaly-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-teal-400/50 bg-[#04181a]/90 backdrop-blur shadow-[0_0_24px_rgba(45,212,191,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-teal-300/90 text-center">
                  Anomaly Detected
                </div>
                <div className="mt-0.5 text-sm text-teal-100 text-center">
                  Press E to Scan
                </div>
              </div>
            </div>
          )}

          {/* Batch B — Surface discoverable prompt (pod / debris / fossil) */}
          {!hudState.inShip && !!hudState.nearDiscovery && !deathSummary && !cityOpen
            && !hudState.city?.nearKiosk && !hudState.nearAnomaly && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-discovery-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-amber-400/50 bg-[#1a1204]/90 backdrop-blur shadow-[0_0_24px_rgba(251,191,36,0.35)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90 text-center">
                  {hudState.nearDiscovery.label}
                </div>
                <div className="mt-0.5 text-sm text-amber-100 text-center">
                  Press E · {hudState.nearDiscovery.verb}
                </div>
              </div>
            </div>
          )}

          {/* Batch B — Discovery lore modal (survivor log / fossil entry) */}
          {discoveryLog && (
            <div
              className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm"
              onClick={() => setDiscoveryLog(null)}
              data-testid="discovery-log-modal"
            >
              <div
                className="relative max-w-md w-[90%] rounded-xl border border-amber-400/40 bg-[#120d05]/95 p-6 shadow-[0_0_40px_rgba(251,191,36,0.25)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                onClick={(ev) => ev.stopPropagation()}
              >
                <div className="text-[11px] tracking-[0.35em] uppercase text-amber-300/80">
                  {discoveryLog.kind === 'fossil' ? 'Xeno-Bestiary Entry'
                    : discoveryLog.kind === 'escape_pod' ? 'Recovered Survivor Log'
                      : 'Salvage Report'}
                </div>
                <h3 className="mt-1 text-lg font-semibold text-amber-100">{discoveryLog.label}</h3>
                <p className="mt-3 text-sm leading-relaxed text-amber-50/90 whitespace-pre-wrap">
                  {discoveryLog.lore}
                </p>
                {(discoveryLog.items?.length || 0) > 0 && (
                  <div className="mt-4 text-xs text-amber-200/80">
                    Recovered: {discoveryLog.items.map((it) => `${it.name}${it.qty > 1 ? ` ×${it.qty}` : ''}`).join(' · ')}
                  </div>
                )}
                <button
                  type="button"
                  className="mt-5 w-full rounded-lg border border-amber-400/50 bg-amber-500/10 py-2 text-sm text-amber-100 hover:bg-amber-500/20 transition-colors"
                  onClick={() => setDiscoveryLog(null)}
                  data-testid="discovery-log-close"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Phase 13 (P3) — NPC talk prompt */}
          {!hudState.inShip && !!hudState.nearPed && !deathSummary && !cityOpen
            && !hudState.city?.nearKiosk && !hudState.nearAnomaly && !hudState.nearStructure && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-npc-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-fuchsia-400/40 bg-[#170818]/90 backdrop-blur shadow-[0_0_22px_rgba(217,70,239,0.2)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-fuchsia-300/90 text-center">
                  Colonist
                </div>
                <div className="mt-0.5 text-sm text-fuchsia-100 text-center" data-testid="hud-npc-name">
                  {hudState.nearPed.name}
                </div>
                <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-slate-300 text-center">
                  Press E · Talk
                </div>
              </div>
            </div>
          )}

          {/* Phase 11 — Structure interact prompt + nanite badge */}
          {hudState.nearStructure && !hudState.inShip && !deathSummary && !structureOverlayDoc && (
            <div
              className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
              data-testid="hud-structure-prompt"
            >
              <div
                className="px-4 py-2 rounded-lg border border-slate-400/40 bg-[#0a0f1a]/85 backdrop-blur shadow-[0_0_24px_rgba(148,163,184,0.18)]"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-slate-300/90 text-center">
                  Structure
                </div>
                <div className="mt-0.5 text-sm text-slate-100 text-center capitalize" data-testid="hud-structure-prompt-name">
                  {hudState.nearStructure.type.replace(/_/g, ' ')}
                </div>
                <button
                  onClick={() => setStructureOverlayDoc(hudState.nearStructure.doc)}
                  className="mt-2 w-full px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-slate-200/90 hover:bg-white text-slate-900 font-semibold transition-colors"
                  data-testid="hud-structure-interact-button"
                >
                  Press E · Interact
                </button>
              </div>
            </div>
          )}
          {/* Death overlay */}
          <DeathOverlay
            open={!!deathSummary}
            summary={deathSummary}
            respawning={respawning}
            onRespawn={async () => {
              setRespawning(true);
              try {
                const { data } = await api.post('/api/player/respawn');
                player.queueStatePatch?.({
                  hp: data?.state?.hp ?? 70,
                  shield: data?.state?.shield ?? 250,
                  oxygen: data?.state?.oxygen ?? 100,
                });
                sceneRef.current?.respawnPlayer?.({
                  hp: data?.state?.hp ?? 70,
                  shield: data?.state?.shield ?? 250,
                });
                setDeathSummary(null);
              } catch (_e) {
                toast.error('Respawn failed — try again');
              } finally {
                setRespawning(false);
              }
            }}
          />

          {/* In-game inventory overlay (toggled by I key + HUD button) */}
          <InventoryOverlay
            open={inventoryOpen}
            onClose={() => setInventoryOpen(false)}
          />

          {/* Phase 9 — City interaction overlay (toggled by E near plaza) */}
          <CityOverlay
            open={cityOpen}
            city={city}
            initialTab={cityInitialTab}
            onClose={() => setCityOpen(false)}
            onAfterAction={handleAfterCityAction}
          />

          {/* Orbital station dock overlay (toggled by E when ship is in a station's dock radius) */}
          <StationOverlay
            open={stationOpen}
            station={dockedStation}
            coordsKey={coords}
            api={api}
            player={player}
            updateCredits={updateCredits}
            onClose={() => { setStationOpen(false); setDockedStation(null); }}
            onOpenHangar={() => { setStationOpen(false); setHangarOpen(true); }}
            onOpenSkills={() => { setStationOpen(false); setSkillTreeOpen(true); }}
          />

          {/* Phase 13 — F1 help overlay */}
          <HelpOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />

          {/* June 2026 — Hangar / Shipyard */}
          <HangarOverlay
            open={hangarOpen}
            api={api}
            credits={user?.credits ?? 0}
            updateCredits={updateCredits}
            onClose={() => setHangarOpen(false)}
            onOpenModules={() => { setHangarOpen(false); setModulesOpen(true); }}
            onSelected={(shipId, def) => {
              setActiveShip(shipId);
              setShipDef(def || shipCatalogRef.current.find((s) => s.id === shipId) || null);
              setHangarOpen(false);
              // Refresh persisted vitals + cargo so the HUD reflects the new
              // ship's stats once the scene rebuilds. Also re-apply the new
              // ship's module loadout multipliers.
              playerRef.current?.refreshInventory?.();
              refreshProgression();
              toast('Switching ship — recalibrating hull…', { icon: '🚀' });
            }}
          />

          {/* Phase 2 — Crafting / Refining (ship Fabricator + Refinery) */}
          <CraftingOverlay
            open={craftingOpen}
            api={api}
            source={craftingSource}
            onClose={() => setCraftingOpen(false)}
            onChanged={() => {
              playerRef.current?.refreshInventory?.();
            }}
          />

          {/* Phase 3 — Derelict ship's-log console (salvage + AI log) */}
          <DerelictOverlay
            open={derelictOpen}
            api={api}
            derelictId={derelictBoardedId}
            updateCredits={updateCredits}
            onSalvaged={() => { playerRef.current?.refreshInventory?.(); }}
            onClose={() => setDerelictOpen(false)}
          />

          {/* Phase 1 — Ship Upgrades / Modules (Outfitting) */}
          <ModulesOverlay
            open={modulesOpen}
            api={api}
            updateCredits={updateCredits}
            onClose={() => setModulesOpen(false)}
            onBackToHangar={() => { setModulesOpen(false); setHangarOpen(true); }}
            onChanged={() => {
              refreshProgression();
              playerRef.current?.refreshState?.();
              playerRef.current?.refreshInventory?.();
            }}
          />

          {/* June 2026 — Skill Tree (Neural Console) */}
          <SkillTreeOverlay
            open={skillTreeOpen}
            api={api}
            credits={user?.credits ?? 0}
            updateCredits={updateCredits}
            onClose={() => setSkillTreeOpen(false)}
            onChanged={() => {
              // Recompute the COMBINED multipliers (skills + modules) live.
              refreshProgression();
              playerRef.current?.refreshState?.();
              playerRef.current?.refreshInventory?.();
            }}
          />

          {/* Phase 10 — Settings (accessible in-game via HUD cog) */}
          <SettingsModal
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
          />

          {/* Phase 11 — Build menu (B key) */}
          <BuildMenu
            open={buildMenuOpen}
            onClose={() => setBuildMenuOpen(false)}
            catalog={naniteCatalog}
            nanites={naniteBalance}
            credits={user?.credits || 0}
            buildState={buildState}
            onPreview={(typeId) => sceneRef.current?.startBuildPreview?.(typeId)}
            onBuyNanitesPack={async () => {
              try {
                const { data } = await api.post('/api/nanites/buy-credits', { packs: 1 });
                if (data?.credits != null) updateCredits(data.credits);
                if (data?.nanites != null) setNaniteBalance(data.nanites);
                toast.success(`+10 nanites · −${data?.cost_credits || 50} cr`);
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Not enough credits');
              }
            }}
            onCommit={async () => {
              const payload = sceneRef.current?.commitBuild?.();
              if (!payload) return;
              try {
                const { data } = await api.post('/api/structures/build', {
                  ...payload,
                  coords_key: coords,
                  planet_idx: Number(planetIdx),
                  moon_idx: moonIdx != null ? Number(moonIdx) : null,
                });
                toast.success(`Built ${data?.type || 'structure'}`);
                sceneRef.current.upsertStructure?.(data);
                setStructures((prev) => [...prev, data]);
                const balRes = await api.get('/api/nanites/balance');
                setNaniteBalance(balRes.data?.nanites ?? 0);
                sceneRef.current.stopBuildPreview?.();
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Build failed');
              }
            }}
            onCancel={() => sceneRef.current?.stopBuildPreview?.()}
          />

          {/* Phase 11 — Structure interaction overlay (E near structure) */}
          <StructureInteractOverlay
            open={!!structureOverlayDoc}
            structure={structureOverlayDoc}
            inventory={player.inventory}
            onClose={() => setStructureOverlayDoc(null)}
            onAfterAction={handleStructureAction}
            onOpenCrafting={() => { setStructureOverlayDoc(null); setCraftingSource('refinery'); setCraftingOpen(true); }}
          />
        </>
      )}

      {/* Inline HUD styles */}
      <style>{`
        .hud-key {
          display: inline-block;
          font-family: 'Source Code Pro', monospace;
          font-size: 10px;
          padding: 1px 6px;
          margin-right: 6px;
          border: 1px solid rgba(24,210,255,0.3);
          border-radius: 4px;
          background: rgba(255,255,255,0.04);
          color: #b8eaff;
          letter-spacing: 0.08em;
        }
      `}</style>
    </div>
  );
}

export default Explore3D;
